import { useState } from "react";
import { api } from "./api";
import { t, KIELEN_NIMI, virheTeksti, kieli, asetaKieli, valittavatKielet, type Kieli } from "./kieli";
import { ApiError } from "./api";

/**
 * T439 (päätös 105): oma käyttöliittymän kieli, tallentuu tunnukselle palvelimelle (`PUT /api/mina/kieli`).
 * Tyhjä valinta = organisaation oletus (`brandi.kieli`). Vaihdon jälkeen sivu ladataan uudelleen, jotta jokainen
 * näkymä piirtyy uudella kielellä eikä ruudulle jää kahta kieltä yhtä aikaa.
 */
export function KieliValinta({ nykyinen }: { nykyinen: string | null }) {
  const [virhe, setVirhe] = useState<string | null>(null);
  const [tallennetaan, setTallennetaan] = useState(false);
  async function vaihda(arvo: string) {
    setVirhe(null); setTallennetaan(true);
    try {
      await api.asetaKieli(arvo === "" ? null : arvo);
      // Ilman uudelleenlatausta (pääChat 11.10.2026): sovellus piirtyy uudelleen uudella kielellä.
      window.dispatchEvent(new CustomEvent("sampo-kieli", { detail: arvo || null }));
      setTallennetaan(false);
    } catch (e) {
      setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e));
      setTallennetaan(false);
    }
  }
  return (
    <section className="kortti" data-kielivalinta="" style={{ marginBottom: 16 }}>
      <label>
        <span className="dim">{t("kieli.otsikko")}</span>{" "}
        <select value={nykyinen ?? ""} disabled={tallennetaan} data-kieli-valitsin=""
                onChange={(e) => void vaihda(e.target.value)}>
          <option value="">{t("kieli.instanssin")}</option>
          {valittavatKielet().map((k) => <option key={k} value={k} lang={k}>{KIELEN_NIMI[k]}</option>)}
        </select>
      </label>
      <p className="dim" style={{ margin: "6px 0 0" }}>{t("kieli.selite")}</p>
      {virhe && <p className="huom huom--vika" role="alert">{virhe}</p>}
    </section>
  );
}

/**
 * Norjan erä (pääChatin toimeksianto Tonin välittämänä 8.10.2026): työnjohto asettaa työntekijän kielen kun tili luodaan
 * tai usealle kerralla — työntekijän ei pidä etsiä kielivalintaa. Tyhjä = organisaation oletus. Pelkkä valitsin: kutsuja
 * päättää milloin arvo tallennetaan.
 */
export function TunnuksenKieli({ arvo, muuta, merkinta }: { arvo: string; muuta: (k: string) => void; merkinta?: string }) {
  return (
    // Toni 20.10.2026 klo 13.02: otsikko kentän yläpuolella vasemmalla (T637), ja otsikko kutistuu sisältönsä levyiseksi (alignSelf) —
    // keskitys venytti T637:n säännöllä valinnan koko rivin levyiseksi (Käyttäjät: 180 → 332/762/991 px, kävelyn kohta D).
    <label style={{ display: "inline-flex", gap: 6, alignSelf: "flex-start" }}>
      <span className="dim">{t("kieli.otsikko")}</span>
      <select value={arvo} data-tunnuksen-kieli={merkinta ?? ""} onChange={(e) => muuta(e.target.value)}>
        <option value="">{t("kieli.instanssin")}</option>
        {valittavatKielet().map((k) => <option key={k} value={k} lang={k}>{KIELEN_NIMI[k]}</option>)}
      </select>
    </label>
  );
}

/**
 * Yläpalkin kielivalikko «FI ▾» (Tonin ehdotus 11.10.2026, pääChatin muotoilema): kaikissa näkymissä ja rooleilla,
 * puhelimella ja tietokoneella. Kielet ovat instanssin sallimat (KIELET), nimet kunkin kielen omalla nimellä, ei lippuja.
 * Valinta tallentuu tunnukselle (PUT /api/mina/kieli, sama kuin Omat tiedot) ja näkymä vaihtuu heti ilman latausta.
 * Natiivi valinta on läpinäkyvänä lyhenteen päällä: suljettuna «FI ▾», avattuna kielten nimet.
 */
export function YlapalkinKieli() {
  const [virhe, setVirhe] = useState<string | null>(null);
  const nyt = kieli();
  const lista = valittavatKielet();
  if (lista.length < 2) return null;
  async function vaihda(k: string) {
    setVirhe(null);
    try {
      await api.asetaKieli(k);
      asetaKieli(k);
      window.dispatchEvent(new CustomEvent("sampo-kieli", { detail: k }));
    } catch (e) { setVirhe(e instanceof ApiError ? virheTeksti(e.virhe, e.message) : String(e)); }
  }
  return (
    <label className="palkki__kieli" data-ylapalkin-kieli={nyt} title={virhe ?? KIELEN_NIMI[nyt as Kieli]}>
      <span aria-hidden="true">{nyt.toUpperCase()} ▾</span>
      <select value={nyt} aria-label={t("kieli.otsikko")} data-ylapalkin-kieli-valinta="" onChange={(e) => void vaihda(e.target.value)}>
        {lista.map((k) => <option key={k} value={k} lang={k}>{KIELEN_NIMI[k]}</option>)}
      </select>
    </label>
  );
}
